import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  ChevronDown, Activity, Terminal, RefreshCw, Loader2,
  ShoppingCart, Search, FileSpreadsheet, Image, Zap,
  CheckCircle2, AlertCircle, Clock, AlertTriangle,
} from "lucide-react";

// ── Sync log helpers ───────────────────────────────────────
const STATUS_MAP = {
  success: { dot: "bg-emerald-500", bg: "bg-emerald-100 dark:bg-emerald-900/30", text: "text-emerald-700 dark:text-emerald-400", label: "نجح" },
  failed:  { dot: "bg-red-500",     bg: "bg-red-100 dark:bg-red-900/30",          text: "text-red-700 dark:text-red-400",         label: "فشل" },
  partial: { dot: "bg-amber-500",   bg: "bg-amber-100 dark:bg-amber-900/30",      text: "text-amber-700 dark:text-amber-400",     label: "جزئي" },
  running: { dot: "bg-[#004EEB] animate-pulse", bg: "bg-blue-100 dark:bg-blue-900/30", text: "text-[#004EEB]", label: "قيد التنفيذ" },
};

function StatusBadge({ status }) {
  const s = STATUS_MAP[status] || STATUS_MAP.partial;
  return (
    <span className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full ${s.bg} ${s.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${s.dot}`} />
      {s.label}
    </span>
  );
}

const fmt = (s) => s ? new Date(s).toLocaleString("ar-SA-u-nu-latn", { hour12: false }) : "—";

function fmtRel(s) {
  if (!s) return "";
  const diff = (Date.now() - new Date(s).getTime()) / 1000;
  if (diff < 60)   return `منذ ${Math.round(diff)} ث`;
  if (diff < 3600) return `منذ ${Math.round(diff / 60)} د`;
  if (diff < 86400)return `منذ ${Math.round(diff / 3600)} س`;
  return `منذ ${Math.round(diff / 86400)} يوم`;
}

function SyncLogRow({ l, open, onToggle }) {
  const isOpen = open === l.id;
  return (
    <div data-testid="sync-log-row" className="border-b border-[var(--border-color)] last:border-0">
      <button
        className="w-full flex flex-wrap md:grid md:grid-cols-12 items-center gap-2 md:gap-4 px-5 md:px-6 py-3.5 text-sm text-start hover:bg-[var(--bg-card-hover)] transition-colors"
        onClick={onToggle}
      >
        <div className="md:col-span-3 mono text-[11px] text-[var(--text-secondary)]">{fmt(l.started_at)}</div>
        <div className="md:col-span-2"><StatusBadge status={l.status} /></div>
        <div className="md:col-span-2 text-xs text-[var(--text-muted)]">
          {l.trigger === "cron" ? "تلقائي" : l.trigger === "webhook" ? "Webhook" : "يدوي"}
        </div>
        <div className="md:col-span-4 flex gap-4 text-xs mono">
          <span className="text-emerald-500 font-bold">+{l.products_updated ?? 0}</span>
          <span className="text-red-400">-{l.products_failed ?? 0}</span>
          <span className="text-[var(--text-muted)]">/{l.products_total ?? 0}</span>
        </div>
        <div className="md:col-span-1 flex md:justify-end">
          <ChevronDown className={`w-4 h-4 text-[var(--text-muted)] transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
        </div>
      </button>

      {isOpen && (
        <div className="px-5 md:px-6 pb-4 pt-2">
          {l.details ? (
            <pre className="bg-[#0C0F18] border border-white/10 text-green-300 text-[11px] p-4 rounded-xl overflow-x-auto leading-relaxed mono whitespace-pre-wrap">
              {typeof l.details === "string" ? l.details : JSON.stringify(l.details, null, 2)}
            </pre>
          ) : (
            <p className="text-[var(--text-muted)] text-xs">لا توجد تفاصيل إضافية.</p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Activity feed helpers ─────────────────────────────────
const FEED_ICONS = {
  sync:       { icon: RefreshCw,       bg: "bg-blue-100 dark:bg-blue-900/40",    ic: "text-blue-600 dark:text-blue-400" },
  seo:        { icon: Search,          bg: "bg-pink-100 dark:bg-pink-900/40",    ic: "text-pink-600 dark:text-pink-400" },
  import:     { icon: FileSpreadsheet, bg: "bg-emerald-100 dark:bg-emerald-900/40", ic: "text-emerald-600 dark:text-emerald-400" },
  alt:        { icon: Image,           bg: "bg-purple-100 dark:bg-purple-900/40",ic: "text-purple-600 dark:text-purple-400" },
  product:    { icon: ShoppingCart,    bg: "bg-amber-100 dark:bg-amber-900/40",  ic: "text-amber-600 dark:text-amber-400" },
  default:    { icon: Zap,             bg: "bg-slate-100 dark:bg-slate-800",     ic: "text-[var(--text-secondary)]" },
};

const FEED_STATUS_STYLES = {
  success:  { icon: CheckCircle2, cls: "text-emerald-500" },
  failed:   { icon: AlertCircle,  cls: "text-red-400" },
  partial:  { icon: AlertTriangle,cls: "text-amber-500" },
  running:  { icon: Clock,        cls: "text-blue-400 animate-pulse" },
};

function ActivityRow({ ev }) {
  // Support both API field names: intelligence API uses {type, label, detail, at, kind}
  // while direct activity_feed may use {category, title, description, timestamp, status}
  const category  = ev.type || ev.category;
  const title     = ev.label || ev.title;
  const description = ev.detail || ev.description;
  const timestamp = ev.at || ev.timestamp;
  const statusKey = ev.kind || ev.status;

  const e = FEED_ICONS[category] || FEED_ICONS.default;
  const StatusIcon = statusKey ? (FEED_STATUS_STYLES[statusKey]?.icon || null) : null;
  const IconComp = e.icon;
  return (
    <div className="flex items-start gap-3 py-3 border-b border-[var(--border-color)] last:border-0">
      <div className={`w-8 h-8 rounded-xl ${e.bg} grid place-items-center shrink-0 mt-0.5`}>
        <IconComp className={`w-4 h-4 ${e.ic}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-[var(--text-primary)]">{title}</span>
          {StatusIcon && (
            <StatusIcon className={`w-3.5 h-3.5 shrink-0 ${FEED_STATUS_STYLES[statusKey]?.cls}`} />
          )}
        </div>
        {description && (
          <p className="text-xs text-[var(--text-secondary)] mt-0.5 leading-relaxed">{description}</p>
        )}
        <p className="text-[10px] text-[var(--text-muted)] mt-1">{fmtRel(timestamp)}</p>
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────
const TABS = [
  { id: "sync",     label: "سجل التزامن", icon: RefreshCw },
  { id: "activity", label: "سجل النشاط",  icon: Activity },
];

export default function SyncLogs() {
  const [activeTab, setActiveTab] = useState("sync");
  const [rows, setRows]           = useState([]);
  const [feed, setFeed]           = useState([]);
  const [open, setOpen]           = useState(null);
  const [loading, setLoading]     = useState(true);

  const loadSync = useCallback(async () => {
    try {
      const { data } = await api.get("/sync/logs", { params: { limit: 100 } });
      setRows(data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  const loadFeed = useCallback(async () => {
    try {
      const { data } = await api.get("/dashboard/intelligence");
      setFeed(data?.activity_feed || data?.activity || []);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    await Promise.allSettled([loadSync(), loadFeed()]);
    setLoading(false);
  }, [loadSync, loadFeed]);

  useEffect(() => {
    loadAll();
    const id = setInterval(loadSync, 6000);
    return () => clearInterval(id);
  }, [loadAll, loadSync]);

  return (
    <div className="min-h-screen" style={{ background: "var(--bg-page)" }}>
      <div className="p-5 md:p-8 max-w-[1400px] mx-auto space-y-6">

        {/* ── Header ── */}
        <header className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-[11px] tracking-[0.18em] uppercase text-[var(--text-muted)] font-bold flex items-center gap-2">
              <Terminal className="w-3 h-3" />
              السجلات
            </p>
            <h1 className="text-2xl md:text-3xl font-black tracking-tight text-[var(--text-primary)] mt-1">
              مركز السجلات
            </h1>
            <p className="text-[var(--text-secondary)] mt-1 text-sm">
              سجل عمليات التزامن وسجل النشاط في مكان واحد.
            </p>
          </div>
          <button
            onClick={loadAll}
            disabled={loading}
            data-testid="logs-refresh-btn"
            className="p-2.5 bg-[var(--bg-card)] border border-[var(--border-color)] rounded-xl text-[var(--text-secondary)] hover:text-[#004EEB] hover:border-[#004EEB]/30 transition-colors"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </button>
        </header>

        {/* ── Tabs ── */}
        <div className="flex gap-1 bg-[var(--bg-card)] border border-[var(--border-color)] rounded-2xl p-1.5 w-fit" data-testid="logs-tabs">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`logs-tab-${tab.id}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all duration-200 ${
                  isActive
                    ? "bg-[#004EEB] text-white shadow-sm"
                    : "text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-card-hover)]"
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {tab.label}
                {tab.id === "sync" && rows.length > 0 && (
                  <span className={`text-[10px] font-black px-1.5 py-0.5 rounded-full ${isActive ? "bg-[var(--bg-card)]/20 text-white" : "bg-slate-100 dark:bg-[var(--bg-card)]/10 text-[var(--text-muted)]"}`}>
                    {rows.length}
                  </span>
                )}
                {tab.id === "activity" && feed.length > 0 && (
                  <span className={`text-[10px] font-black px-1.5 py-0.5 rounded-full ${isActive ? "bg-[var(--bg-card)]/20 text-white" : "bg-slate-100 dark:bg-[var(--bg-card)]/10 text-[var(--text-muted)]"}`}>
                    {feed.length}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* ══════════════════════════════════════════
            TAB: سجل التزامن
        ══════════════════════════════════════════ */}
        {activeTab === "sync" && (
          <div className="bg-[var(--bg-card)] rounded-2xl border border-[var(--border-color)] overflow-hidden shadow-sm" data-testid="sync-logs-container">
            {/* Table header */}
            <div className="hidden md:grid md:grid-cols-12 gap-4 px-6 py-3 border-b border-[var(--border-color)] bg-[var(--bg-card-hover)]">
              {["الوقت", "الحالة", "المصدر", "المنتجات", ""].map((h, i) => (
                <div key={i} className={`text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-widest ${
                  i === 0 ? "col-span-3" : i === 1 ? "col-span-2" : i === 2 ? "col-span-2" : i === 3 ? "col-span-4" : "col-span-1"
                }`}>{h}</div>
              ))}
            </div>

            {loading && rows.length === 0 ? (
              <div className="py-16 flex items-center justify-center gap-3 text-[var(--text-muted)]">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span className="text-sm">جاري التحميل...</span>
              </div>
            ) : rows.length === 0 ? (
              <div className="py-16 text-center">
                <RefreshCw className="w-8 h-8 text-[var(--text-muted)] mx-auto mb-3" />
                <p className="text-sm text-[var(--text-muted)]">لا توجد سجلات بعد.</p>
              </div>
            ) : (
              rows.map((l) => (
                <SyncLogRow
                  key={l.id}
                  l={l}
                  open={open}
                  onToggle={() => setOpen(open === l.id ? null : l.id)}
                />
              ))
            )}
          </div>
        )}

        {/* ══════════════════════════════════════════
            TAB: سجل النشاط
        ══════════════════════════════════════════ */}
        {activeTab === "activity" && (
          <div className="bg-[var(--bg-card)] rounded-2xl border border-[var(--border-color)] overflow-hidden shadow-sm" data-testid="activity-feed-container">
            <div className="px-6 py-4 border-b border-[var(--border-color)] flex items-center justify-between bg-[var(--bg-card-hover)]">
              <div>
                <h3 className="text-sm font-bold text-[var(--text-primary)]">سجل النشاط الموحّد</h3>
                <p className="text-[11px] text-[var(--text-muted)] mt-0.5">أحدث {feed.length} حدث من التزامن، SEO، Excel، وصور ALT</p>
              </div>
              <button onClick={loadFeed} className="text-[var(--text-muted)] hover:text-[#004EEB] transition-colors">
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>

            <div className="divide-y divide-[var(--border-color)] px-5 py-2">
              {loading && feed.length === 0 ? (
                <div className="py-12 flex items-center justify-center gap-3 text-[var(--text-muted)]">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span className="text-sm">جاري التحميل...</span>
                </div>
              ) : feed.length === 0 ? (
                <div className="py-12 text-center">
                  <Activity className="w-8 h-8 text-[var(--text-muted)] mx-auto mb-3" />
                  <p className="text-sm text-[var(--text-muted)]">لا توجد أحداث بعد.</p>
                </div>
              ) : (
                feed.map((ev, i) => <ActivityRow key={i} ev={ev} />)
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
